import Head from 'next/head';
import type { GetServerSideProps, GetServerSidePropsContext } from 'next';
import type { ReactNode } from 'react';

import { getSingleTranslation } from 'actions/general';

import { Layout } from 'components/layout';
import { availableSorts, DEFAULT_SORT } from 'utils/filters';
import { getSeoRelLinks } from 'components/grid/paginator';
import { baseTranslations } from 'utils/translations';
import { getProducts } from 'actions/products';
import { filterTransformer, DEFAULT_LIMIT, getRealID, COOKIES, handleCanonicalUrl } from 'utils/helpers';
import { initCMS } from 'utils/cms';
import { TIER_POSITIONS } from 'utils/constants/cms';
import type { TFilter } from 'types/product-list';

import { getGridProps } from 'actions/products';
import { getSeo } from 'actions/general';
import { ProductsListPage } from 'components/grid/product-list-page';
import { getDomainValue } from 'utils/domain';
import type { TProductListPage } from 'types/product-list';
import { parseNextQuery } from 'utils/next';
import { parseLocale } from 'hooks/useLocale';

// Utilidades de guards y conversiones

type SortKey = 'default' | 'price_asc' | 'price_desc';
const isSortKey = (value: unknown, list: readonly string[]): value is SortKey => {
  return typeof value === 'string' && list.includes(value);
};

const toSafeNumber = (value: unknown, fallback: number): number => {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : fallback;
};

export const getServerSideProps: GetServerSideProps<any> = async (context: GetServerSidePropsContext) => {
  const { locale, language } = parseLocale(context.locale ?? '');
  const urlParams = parseNextQuery(context.query);
  const target = urlParams.get('target');
  const product = urlParams.get('product');
  const page = urlParams.get('page');
  const sort = urlParams.get('sort');
  const targets = Object.values(baseTranslations.target).flat();
  const families = Object.values(baseTranslations.product).flat();

  if (!target || !product || !targets.includes(target) || !families.includes(product)) {
    return {
      notFound: true,
    };
  }

  urlParams.delete('target');
  urlParams.delete('product');

  const filters: Record<string, string> = {};
  for (const [key, value] of urlParams.entries()) {
    if (key.startsWith('filter.')) {
      filters[key] = value;
    }
  }

  const hasFiltersInURL = Object.keys(filters).length > 0;

  const resolvedUrl = context.resolvedUrl || '';
  const targetRealID = getRealID('target', target);
  const familyRealID = getRealID('product', product);
  const cache = urlParams.get('cache');

  try {
    const langKeyTarget = language as keyof typeof baseTranslations.target;
    const langKeyProduct = language as keyof typeof baseTranslations.product;
    const isLangTarget = baseTranslations.target[langKeyTarget]?.includes(target!);
    const isLangProduct = baseTranslations.product[langKeyProduct]?.includes(product!);
    const gridProps = await getGridProps({
      context,
      locale: locale,
      ...(cache ? { cache } : {}),
      targetId: targetRealID,
      familyId: familyRealID,
      slug: '',
      filters,
      langGenderOrFamily: !(isLangTarget && isLangProduct),
    });

    const newCanonicalUrl = handleCanonicalUrl(locale, context.query, resolvedUrl, gridProps);

    if (newCanonicalUrl) {
      const { res } = context;

      res.setHeader('Cache-Control', 's-maxage=3600');
      res.statusCode = 307;
      res.setHeader('Location', newCanonicalUrl);

      res.end();

      return { props: {} };
    }
  } catch (error) {
    // Error controlado para no romper SSR
    console.error('Error retrieving gridProps', JSON.stringify(error));
  }

  try {
    const pageNumber = toSafeNumber(page, 1);
    const pageOffset = pageNumber > 1 ? (pageNumber - 1) * DEFAULT_LIMIT : 0;
    const appliedSort = isSortKey(sort, availableSorts) && sort !== DEFAULT_SORT ? sort : undefined;
    const targetStr = target!;
    const productStr = product!;
    const previousPagePromise =
      pageOffset === 0 ?
        Promise.resolve(null)
      : getProducts({
          context,
          lang: locale,
          target: targetStr,
          page: pageOffset - DEFAULT_LIMIT,
          filters,
          ...(appliedSort ? { sort: appliedSort } : {}),
          product: productStr,
          square: getDomainValue({ camper: true, camperlab: false, nnormal: true }),
        });
    const nextPagePromise =
      pageOffset === 0 ?
        Promise.resolve(null)
      : getProducts({
          context,
          lang: locale,
          target: targetStr,
          page: pageOffset + DEFAULT_LIMIT,
          filters,
          ...(appliedSort ? { sort: appliedSort } : {}),
          product: productStr,
          square: getDomainValue({ camper: true, camperlab: false, nnormal: true }),
        });

    const [response, previousPage, nextPage] = await Promise.all([
      getProducts({
        context,
        lang: locale,
        target: targetStr,
        page: pageOffset,
        filters,
        ...(appliedSort ? { sort: appliedSort } : {}),
        product: productStr,
        seoContent: true,
        square: getDomainValue({ camper: true, camperlab: false, nnormal: true }),
      }),
      previousPagePromise,
      nextPagePromise,
    ]);

    const {
      products,
      total,
      availableFilters,
      currentList,
      currentListId,
      camperlab,
      title,
      description,
      urlCanonical,
      urlCanonicalGTM,
      seoContent,
      rewalk,
      relatedCollectionId,
      urlRelatedCollection,
      vmtoolConfig,
    } = response;
    // No devolver un 404, ya que aunque la grid esté vacía es posible que haya Tiers
    /* if (products.length === 0) {
      return {
        notFound: true,
      };
    } */
    let baseAvailable = availableFilters;
    if (hasFiltersInURL) {
      const collectionFromUrl = filters['filter.collection'];
      const sustainabilityFromUrl = filters['filter.sustainability'];
      const baseRequest = await getProducts({
        context,
        lang: locale,
        target: targetStr,
        product: productStr,
        page: 0,
        limit: 1,
        filters:
          collectionFromUrl ? { 'filter.collection': collectionFromUrl }
          : sustainabilityFromUrl ? { 'filter.sustainability': sustainabilityFromUrl }
          : null,
      } as any);
      baseAvailable = baseRequest.availableFilters;
    }

    // //////////////// Tiers

    // [TODO]: Temporal. Determinar que tipo de tier es:
    //         - collection, line, concept, typology, sustainability....
    // En funcion del rewrites y el filter que traiga.
    let tierType = 'grid';
    let cmsCode = '';
    let gridId = 'see-all';

    if (hasFiltersInURL) {
      const collectionFromUrl = filters['filter.collection'];
      const sustainabilityFromUrl = filters['filter.sustainability'];
      const typologyFromUrl = filters['filter.typology'];
      const conceptFromUrl = filters['filter.concept'];

      if (typologyFromUrl) {
        tierType = 'typology';
        cmsCode = typologyFromUrl;
        gridId = '';
      } else if (sustainabilityFromUrl) {
        tierType = 'sustainability';
        cmsCode = sustainabilityFromUrl;
        gridId = '';
      } else if (collectionFromUrl) {
        tierType = 'collection';
        cmsCode = collectionFromUrl;
        gridId = '';
      } else if (conceptFromUrl) {
        tierType = 'concept';
        cmsCode = conceptFromUrl;
        gridId = '';
      }
    }
    const hasSubgrid = relatedCollectionId !== null && relatedCollectionId !== undefined && relatedCollectionId !== '';
    let subgridUrlCollection = '';
    let subgridProducts: any[] = [];
    if (hasSubgrid) {
      const resolvedUrlWithoutQuery = resolvedUrl.split('?')[0];
      subgridUrlCollection = urlRelatedCollection || `/${locale}${resolvedUrlWithoutQuery}?filter.collection=${relatedCollectionId}`;
      const subgridResponse = await getProducts({
        context,
        lang: locale,
        target: targetStr,
        page: 0,
        limit: 8,
        filters: { 'filter.collection': relatedCollectionId },
        product: productStr,
        seoContent: false,
        square: getDomainValue({ camper: true, camperlab: false, nnormal: true }),
      });
      subgridProducts = subgridResponse.products.slice(0, 8);
    }
    // //////////////// Fin Tiers
    const { defaultProps, tiers, cmsSeoContent } = await initCMS({
      context,
      locale: locale,
      // Ajuste de tipos esperados por el CMS: target como ID original y family como number
      tierParams: [tierType, [cmsCode], targetRealID, Number(familyRealID), gridId],
      // tnmSelectedTarget: targetRealID,
      seoContent: camperlab ? null : { target: targetRealID },
      camperlab,
    });

    const appliedFilters = (Object.keys(filters).length > 0 ? filterTransformer.decode(filters, availableFilters) : {}) as Record<string, TFilter[]>;
    const filtersCount = Object.keys(appliedFilters).length > 0 ? Object.keys(appliedFilters).reduce((a, b) => (appliedFilters[b]?.length ?? 0) + a, 0) : 0;

    // check if any applied filter is of type quality then extract that and push it into appliedFilers.qualities

    if (availableFilters.qualities) {
      Object.keys(appliedFilters).forEach((key) => {
        const qualityFilter = appliedFilters[key]?.find((filter) => filter.type === 'qualities');

        if (qualityFilter) {
          const indexOfQuality = appliedFilters[key]?.findIndex((filter) => filter.key === qualityFilter?.key);

          appliedFilters.qualities = [];
          appliedFilters.qualities.push(qualityFilter);

          if (appliedFilters[key] && indexOfQuality !== undefined && indexOfQuality !== -1) {
            appliedFilters[key].splice(indexOfQuality, 1);
          }
        }
      });
    }

    if (appliedFilters.sustainabilities) {
      const { sustainabilities } = appliedFilters;

      sustainabilities.forEach(async (filterItem) => {
        if (filterItem.key === filterItem.label) {
          // si la key y la label son iguales significa que no lo tiene
          // pedimos la label por single
          const labelForFilter = (await getSingleTranslation(locale, 'menu.lateral', filterItem.key)) as string;

          if (labelForFilter) {
            filterItem.label = labelForFilter;
          }
        }
      });
    }

    const gtmProps = {
      target,
      targetRealID,
      product,
      familyRealID,
      currentList: currentList ?? '',
      currentListId,
      camperlab,
      currentListName: title ?? '',
      page: pageNumber - 1,
      url: urlCanonicalGTM || null,
    };

    const queryParams = [
      `profile=${locale}`,
      `family=${String(familyRealID || '1')}`,
      `gender=${String(targetRealID || 'X')}`,
      `option=${String(targetRealID || 'X')}`,
      'page=PRODUCTOS',
    ];
    const collectionFilter = filters['filter.collection'];
    const conceptFilter = filters['filter.concept'];
    const typologyFilter = filters['filter.typology'];
    const sustainabilityFilter = filters['filter.sustainability'];
    if (collectionFilter) {
      queryParams.push(`specialCollection=${collectionFilter}`);
    }
    if (conceptFilter) {
      queryParams.push(`collection=${conceptFilter}`);
    }
    if (typologyFilter) {
      queryParams.push(`typology=${typologyFilter}`);
    }
    if (sustainabilityFilter) {
      queryParams.push(`sustainability=${sustainabilityFilter}`);
    }
    const metatags = await getSeo(context, queryParams);
    const breadcrumbI18n = {
      target: String(targetRealID).toLocaleLowerCase() || null,
      family: (familyRealID as any) || null,
    };

    const originalCta: { text?: string; fontColor?: string } = {};
    if (tiers?.standalone && tiers?.standalone?.ctaText) {
      originalCta.text = tiers.standalone.ctaText;
      if (tiers.standalone.ctaTextProps) {
        originalCta.fontColor = tiers.standalone.ctaTextProps.fontColor || tiers?.textProps?.fontColor || '#000000';
        tiers.standalone.ctaTextProps.fontColor = '#ffffff00';
      }
    }
    const isApp = context?.req?.cookies[COOKIES.APP_IPAD] === 'true' || false;
    return {
      props: {
        defaultProps,
        hrefLangLinks: null,
        currentList: currentList || null,
        firstProducts: products,
        previousPageProducts: previousPage?.products ?? null,
        nextPageProducts: nextPage && Array.isArray(nextPage.products) && nextPage.products.length > 0 ? nextPage.products : null,
        total,
        availableFilters,
        baseAvailable,
        appliedFilters,
        defaultSort: isSortKey(sort, availableSorts) ? sort : DEFAULT_SORT,
        filtersCount,
        hasFiltersInURL,
        gtmProps,
        tierParams: [tierType, [cmsCode], targetRealID, familyRealID, gridId],
        tiersProps: !isApp && tiers ? tiers : {},
        initialPage: pageNumber || 1,
        camperlab,
        title: title || null,
        description: description || null,
        metatags,
        breadcrumbI18n,
        urlCanonical,
        originalCta,
        resolvedUrl,
        recycled: rewalk ?? false,
        seoContent:
          seoContent ?
            {
              ...seoContent,
              cms:
                cmsSeoContent ?
                  {
                    ...cmsSeoContent,
                    gridName: currentList ?? title ?? '',
                  }
                : null,
            }
          : null,
        subgridProducts,
        subgridUrlCollection,
        isApp,
        vmtoolConfig,
        hasFiltersUrl: hasFiltersInURL,
        totalPages: Math.ceil(total / DEFAULT_LIMIT),
      },
    };
  } catch (e) {
    console.error(e);
    context.res.statusCode = 500;
    return {
      props: {
        error: `${context.resolvedUrl} SSR Error: ${e}`,
        permanent: false,
      },
    };
  }
};

type WithLayoutProps = {
  // Nota: tipos amplios por depender de props globales del layout del proyecto
  defaultProps: any;
  children: ReactNode;
  breadcrumbI18n: any;
  camperlab: any;
  recycled: boolean;
  isApp: boolean;
  breadcrumbsBottom?: boolean;
  breadcrumbs?: any;
};

function WithLayout({ defaultProps, children, breadcrumbI18n, camperlab, recycled, isApp, breadcrumbsBottom, breadcrumbs }: WithLayoutProps) {
  if (isApp) {
    return <>{children}</>;
  }

  return (
    <Layout
      {...defaultProps}
      breadcrumbI18n={breadcrumbI18n}
      breadcrumbs={breadcrumbs}
      breadcrumbsBottom={breadcrumbsBottom}
      scrollArrow={true}
      camperlab={camperlab}
      recycled={recycled}
    >
      {children}
    </Layout>
  );
}

export default function ProductsList({
  defaultProps,
  hrefLangLinks,
  breadcrumbI18n,
  gtmProps,
  camperlab,
  metatags,
  recycled,
  isApp,
  ...rest
}: TProductListPage) {
  const hasStandalone = rest.tiersProps[TIER_POSITIONS.STANDALONE] !== null && rest.tiersProps[TIER_POSITIONS.STANDALONE] !== undefined;
  const hasTitleDescription = rest.title !== null && rest.title !== undefined && rest.description !== null && rest.description !== undefined;
  const isGridSeeAll = rest.filtersCount === 0 && !rest.hasFiltersUrl;

  return (
    <WithLayout
      defaultProps={{
        ...defaultProps,
        metatags,
        pageName: 'grid',
        gtmProps: { ...gtmProps, products: rest.firstProducts },
      }}
      isApp={isApp}
      breadcrumbs={getDomainValue({ camper: true, nnormal: true, camperlab: false })}
      breadcrumbI18n={getDomainValue({ camper: breadcrumbI18n, nnormal: breadcrumbI18n, camperlab: null })}
      breadcrumbsBottom={hasStandalone || hasTitleDescription}
      camperlab={camperlab}
      recycled={recycled}
    >
      <Head>
        {getSeoRelLinks(rest.initialPage, rest.totalPages, rest.urlCanonical, isGridSeeAll)}
        {hrefLangLinks?.map((l) => (
          <link key={`hreflang-${l.hrefLang}`} rel="alternate" hrefLang={l.hrefLang} href={l.url} />
        ))}
      </Head>
      <ProductsListPage {...rest} isApp={isApp} gtmProps={gtmProps} camperlab={camperlab} breadcrumbI18n={breadcrumbI18n} />
    </WithLayout>
  );
}

ProductsList.i18nPage = 'grid';

// probar props
